import activityEndpoints from '@/lib/api/endpoints/activityEndpoints';
import type { Activity } from '@/lib/models/activity';
import { ActivitySchema } from '@/lib/models/activity';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';

/**
 * Interfaccia per il service delle Activities
 * Wrapper che estrae automaticamente i dati dalle response Axios
 */
export interface ActivitiesService {
	/**
	 * Recupera la lista delle attività con query OData
	 * @param query - Query string OData (es: "?$top=25&$count=true&$orderby=description asc")
	 * @returns Promise con response OData contenente array di Activity
	 */
	getActivities(query: string): Promise<ODataResponse<Activity>>;

	/**
	 * Recupera una singola attività per ID
	 * @param id - ID dell'attività
	 * @returns Promise con singola Activity
	 */
	getActivity(id: number): Promise<Activity>;

	/**
	 * Aggiorna un'attività esistente
	 * @param id - ID dell'attività da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con Activity aggiornata
	 */
	updateActivity(id: number, payload: Activity): Promise<Activity>;

	/**
	 * Crea una nuova attività
	 * @param payload - Dati della nuova attività
	 * @returns Promise con Activity creata
	 */
	postActivity(payload: Activity): Promise<Activity>;

	/**
	 * Elimina un'attività
	 * @param id - ID dell'attività da eliminare
	 * @returns Promise void
	 */
	deleteActivity(id: number): Promise<void>;
}

/**
 * Implementazione del service per le Activities
 * Estrae automaticamente la proprietà .data dalle response Axios
 */
const activitiesService: ActivitiesService = {
	async getActivities(query: string): Promise<ODataResponse<Activity>> {
		const response = (await activityEndpoints.getActivities(query)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateODataResponse(response, ActivitySchema, 'Activity.getAll');
		return response;
	},

	async getActivity(id: number): Promise<Activity> {
		const rawData = (await activityEndpoints.getActivity(id)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, ActivitySchema, `Activity.getById(${id})`);
		return rawData;
	},

	async updateActivity(id: number, payload: Activity): Promise<Activity> {
		const rawData = (await activityEndpoints.updateActivity(id, payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, ActivitySchema, `Activity.updateById(${id})`);
		return rawData;
	},

	async postActivity(payload: Activity): Promise<Activity> {
		const rawData = (await activityEndpoints.postActivity(payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, ActivitySchema, 'Activity.create');
		return rawData;
	},

	async deleteActivity(id: number): Promise<void> {
		return (await activityEndpoints.deleteActivity(id)).data;
	},
};

export default activitiesService;
